import { AURORA_COUNTRIES, AURORA_REPORTED_COUNTRIES as COUNTRY_TOTAL, Flag } from "../../components/flags.jsx";
import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { ArrowUpRight } from "@phosphor-icons/react";
import { ClipPhoto, MaskWords, PageFrame, Photo, Seo } from "../../kit.jsx";
import { Btn, Count, GoLink, Rise, clamp01 } from "./pgkit.jsx";
import "./programmes.css";

const DEVPOST = "https://aurora-global-hackathon-grid.devpost.com/";
const SITE = "https://aurora.projectgrid.org/";

const COUNTRIES = AURORA_COUNTRIES.map((country) => [country.code, country.name]);
const MORE = COUNTRY_TOTAL - COUNTRIES.length;
const CELLS = COUNTRIES.length + 1;

const STAGES = [
  ["Idea round", "Teams entered with an idea for a real problem."],
  ["Prototype", "Teams turned the idea into something that works."],
  ["Live pitch", "Teams presented their prototypes live."],
  ["Incubation", "The Top 20 teams moved on to incubation."],
];

const RECORD = [
  ["Organiser", "Project GRID"],
  ["Format", "Online, designed so students with limited resources and slow internet could take part"],
  ["Who could join", "Students aged 14 and above"],
  ["Participants", "1,500"],
  ["Countries", "27+"],
  ["Prizes", "₹1 lakh in prizes, given as HP laptops to the winners"],
  ["Outcome", "The Top 20 teams entered incubation"],
];

function Hero() {
  return (
    <section className="pg-auh" aria-labelledby="pg-auh-title">
      <Rise as="p" className="gh-mono pg-kicker" onMount>Aurora · Online · A Project GRID programme</Rise>
      <MaskWords as="h1" id="pg-auh-title" text="A global hackathon, run by students." onMount delay={0.05} />
      <div className="pg-auh__row">
        <Rise as="p" onMount delay={0.35}>
          Aurora was an online hackathon organised by Project GRID. Teams moved from an idea round to prototypes and live pitches, and the Top 20 entered incubation. It was built so students with limited resources and slow internet could still take part.
        </Rise>
        <Rise className="gh-actions" onMount delay={0.45}>
          <Btn href={DEVPOST} icon={<ArrowUpRight weight="bold" aria-hidden="true" className="pg-btn__after" />}>See it on Devpost</Btn>
          <Btn href={SITE} variant="ghost" icon={<ArrowUpRight weight="bold" aria-hidden="true" className="pg-btn__after" />}>aurora.projectgrid.org</Btn>
        </Rise>
      </div>
      <ul className="pg-auh__stats" aria-label="Aurora in figures">
        <li><strong><Count to={27} suffix="+" /></strong><span className="pg-auh__label">countries</span></li>
        <li><strong><Count to={1500} /></strong><span className="pg-auh__label">participants</span></li>
        <li><strong><Count to={1} prefix="₹" /><small> lakh</small></strong><span className="pg-auh__label">in prize laptops</span></li>
      </ul>
    </section>
  );
}

function FlagCell({ country, index, progress, reduceMotion }) {
  const start = (index / CELLS) * 0.75;
  const t = useTransform(progress, (value) => clamp01((value - start) / 0.18));
  const opacity = useTransform(t, (value) => value);
  const rotateX = useTransform(t, (value) => (1 - value) * -80);
  const style = reduceMotion ? undefined : { opacity, rotateX };
  if (!country) {
    return (
      <motion.li className="pg-flag pg-flag--more" style={style}>
        <span className="pg-flag__more">+{MORE}</span>
        <span className="pg-flag__name">more countries</span>
      </motion.li>
    );
  }
  return (
    <motion.li className="pg-flag" style={style}>
      <Flag code={country[0]} className="pg-flag__svg" />
      <span className="pg-flag__name">{country[1]}</span>
    </motion.li>
  );
}

function FlagField() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.9", "end 0.55"] });
  // Counts with the flags as they land, then settles on the total once the last tile is in.
  const named = useTransform(scrollYProgress, (value) => {
    const t = clamp01(value / 0.7);
    return t >= 1 ? `${COUNTRY_TOTAL}+` : `${Math.round(t * COUNTRY_TOTAL)}`;
  });
  const cells = [...COUNTRIES, null];
  return (
    <section className="pg-flags" aria-labelledby="pg-flags-title">
      <header className="pg-flags__head">
        <MaskWords id="pg-flags-title" text="Where the teams came from." />
        <div className="pg-flags__count" aria-hidden="true">
          <strong>{reduceMotion ? `${COUNTRY_TOTAL}+` : <motion.span>{named}</motion.span>}</strong>
          <span className="pg-flags__label">countries</span>
        </div>
      </header>
      <ul className="pg-flags__grid" ref={ref} aria-label={`Aurora countries, ${COUNTRY_TOTAL}+ in total`}>
        {cells.map((country, index) => (
          <FlagCell key={country ? country[1] : "more"} country={country} index={index} progress={scrollYProgress} reduceMotion={reduceMotion} />
        ))}
      </ul>
      <p className="pg-flags__note">
        Aurora ran online, and its participants came from {COUNTRY_TOTAL}+ countries.
      </p>
    </section>
  );
}

function Journey() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.8", "end 0.55"] });
  const draw = useTransform(scrollYProgress, (value) => clamp01(value));
  return (
    <section className="pg-journey" aria-labelledby="pg-journey-title">
      <header className="pg-journey__head">
        <MaskWords id="pg-journey-title" text="How Aurora worked." />
        <p>Four stages, from a first idea to incubation for the strongest teams.</p>
      </header>
      <div className="pg-journey__track" ref={ref}>
        <div className="pg-journey__rail" aria-hidden="true">
          <motion.span style={reduceMotion ? undefined : { "--draw": draw }} />
        </div>
        <ol>
          {STAGES.map(([title, text], index) => (
            <Rise as="li" key={title} delay={index * 0.1}>
              <span className="pg-journey__dot" aria-hidden="true" />
              <span className="gh-mono">Stage {index + 1}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </Rise>
          ))}
        </ol>
      </div>
      <blockquote className="pg-journey__quote">
        <p>"Focus on real skills and experience, not just keywords."</p>
        <cite>Aurora&apos;s aim</cite>
      </blockquote>
    </section>
  );
}

function Winners() {
  return (
    <section className="pg-winners" aria-labelledby="pg-winners-title">
      <header className="pg-winners__head">
        <MaskWords id="pg-winners-title" text="The results." />
        <p>Two of the award winners, and the certificates behind the Top 20.</p>
      </header>

      <article className="pg-winner">
        <ClipPhoto
          className="pg-winner__portrait"
          src="/images/grid-photo-10-p8.jpg"
          alt="Jerovin Floyd Vincent Joseph holding his Aurora prize laptop"
          caption="Jerovin with his Aurora winner laptop."
          sizes="(max-width: 899px) 100vw, 40vw"
        />
        <div className="pg-winner__copy">
          <span className="pg-award">Overall winner</span>
          <h3>Jerovin Floyd Vincent Joseph</h3>
          <p className="pg-winner__project">ChillGui</p>
          <p>Jerovin, a student at IIIT Delhi, built ChillGui on his own. It is an Edge-AI layer that simplifies everyday app interfaces for older users. The idea came from helping older family members use their smartphones.</p>
          <dl className="pg-winner__facts">
            <div><dt>Team</dt><dd>Solo builder</dd></div>
            <div><dt>Focus</dt><dd>Accessibility</dd></div>
            <div><dt>Prize</dt><dd>Winner laptop</dd></div>
          </dl>
          <div className="pg-winner__evidence">
            <figure>
              <span><Photo src="/images/evidence/aurora-winner-01.jpg" alt="The ChillGui project logo" sizes="240px" /></span>
              <figcaption>The ChillGui project mark.</figcaption>
            </figure>
            <figure>
              <span><Photo src="/images/evidence/aurora-winner-02.jpg" alt="Aurora overall-winner certificate for Jerovin Floyd" sizes="240px" /></span>
              <figcaption>His overall-winner certificate.</figcaption>
            </figure>
          </div>
        </div>
      </article>

      <article className="pg-winner pg-winner--flip">
        <ClipPhoto
          className="pg-winner__portrait"
          src="/images/grid-photo-11-p8.jpg"
          alt="Gantavya Rohilla holding his Aurora prize laptop"
          caption="Gantavya with his Aurora prize laptop."
          sizes="(max-width: 899px) 100vw, 40vw"
        />
        <div className="pg-winner__copy">
          <span className="pg-award pg-award--line">Best Innovation</span>
          <h3>Gantavya Rohilla</h3>
          <p className="pg-winner__project">A personal handwriting model</p>
          <p>Gantavya, a third-year student at IIT Madras, built a model that learns a user&apos;s handwriting and can do calculations, with the aim of moving note-taking into physical space. He is exploring smart-glasses integration.</p>
        </div>
      </article>

      <div className="pg-top20">
        <div>
          <span className="pg-award pg-award--line">Top 20</span>
          <h3>The Top 20 went on to incubation.</h3>
          <p>Builders who reached the Top 20 received a certificate of appreciation signed by GRID&apos;s co-founders, and moved on to the incubation stage.</p>
        </div>
        <figure>
          <span><Photo src="/images/evidence/aurora-top20-team.jpg" alt="Aurora Top 20 certificate of appreciation for Siddhant Dasgupta" sizes="(max-width: 899px) 100vw, 40vw" /></span>
          <figcaption>A Top 20 certificate, awarded to Siddhant Dasgupta.</figcaption>
        </figure>
      </div>
    </section>
  );
}

function Record() {
  return (
    <section className="pg-record" aria-labelledby="pg-record-title">
      <div className="pg-record__head">
        <MaskWords id="pg-record-title" text="Aurora at a glance." />
        <p>The essentials, in one place.</p>
      </div>
      <dl className="pg-record__list">
        {RECORD.map(([term, value], index) => (
          <Rise key={term} delay={index * 0.03}>
            <dt>{term}</dt>
            <dd>{value}</dd>
          </Rise>
        ))}
      </dl>
    </section>
  );
}

function Onward() {
  return (
    <section className="pg-auend" aria-labelledby="pg-auend-title">
      <MaskWords id="pg-auend-title" text="More on Aurora." />
      <ul className="pg-bigrows">
        <li><a href={DEVPOST} target="_blank" rel="noreferrer"><span>Devpost</span><small>Rules, prize categories and submitted projects</small><ArrowUpRight weight="bold" aria-hidden="true" /></a></li>
        <li><a href={SITE} target="_blank" rel="noreferrer"><span>aurora.projectgrid.org</span><small>The official Aurora site, for the next announcement</small><ArrowUpRight weight="bold" aria-hidden="true" /></a></li>
      </ul>
      <div className="gh-actions">
        <GoLink to="/programmes/upcoming">Upcoming events</GoLink>
        <GoLink to="/programmes">All programmes</GoLink>
      </div>
    </section>
  );
}

export default function AuroraPage() {
  return (
    <PageFrame className="rd-page pg-page pg-aurora-page">
      <Seo
        title="Aurora"
        path="/programmes/aurora"
        description="A year-long global hackathon and changemaking festival for students in Grades 8-12, designed with remote and under-resourced learners in mind."
      />
      <main className="pg-aurora">
        <Hero />
        <FlagField />
        <Journey />
        <Winners />
        <Record />
        <Onward />
      </main>
    </PageFrame>
  );
}
